import { useEffect, useState, useMemo, useRef } from "react";
import { ReportBackButton } from "../ledgerReports/ReportBackButton";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { globalStyle, handleDangerVisible } from "../../global/Utils";
import { getCompany } from "../../Actions/CompanyAction";
import { InputField } from "../Common/InputField";
import { getInvoiceSummary } from "../../Actions/InvoicesAction";
import {
  Group as FaUserFriends,
  PersonAdd as FaUserPlus,
  PersonRemove as FaUserMinus,
  PersonOff as FaUserSlash,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import MobileViewTabelList from "../Common/MobileViewTabelList";

import { upperCaseInput } from "../../global/textCase";
import { formatTwoDecimals } from "../../global/money";
import { SearchableSelect } from "../Common/SearchableSelect";
const InvoiceSummary = () => {
  const dispatch = useDispatch();
  const today = new Date();
  const [hasSearched, setHasSearched] = useState(false);
  const formRef = useRef(null);
  const maxDate = today.toISOString().split("T")[0];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isGettingInvoiceSummary = useSelector(
    (state) => state.InvoicesListReducer?.isGettingInvoiceSummary,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const {
    uniqueBuyersCount,
    subTotalSum,
    totalResults,
    grandTotalSum,
    buyerSummary,
  } = useSelector((state) => state.InvoicesListReducer?.invoiceSummary);

  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    companyId: "",
  });

  const [errors, setErrors] = useState({});
  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = ["startDate", "endDate", "companyId"];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = "This field is required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    setHasSearched(true);
    dispatch(getInvoiceSummary(formData, setFormData, handleDangerVisible));
  };
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getCompany("", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);
  const stats = [
    {
      icon: <FaUserFriends size={24} className="text-blue-700" />,
      title: "Total Buyer",
      value: uniqueBuyersCount,
      change: "",
      color: "bg-blue-100",
      textColor: "text-blue-700",
      graphColor: "border-blue-700",
    },
    {
      icon: <FaUserPlus size={24} className="text-purple-700" />,
      title: "Total Amount (INC. Tax)",
      value: subTotalSum,
      change: "",
      color: "bg-purple-100",
      textColor: "text-purple-700",
      graphColor: "border-purple-700",
    },
    {
      icon: <FaUserMinus size={24} className="text-yellow-700" />,
      title: "Submitted Invoices",
      value: totalResults,
      change: "",
      color: "bg-yellow-100",
      textColor: "text-yellow-700",
      graphColor: "border-yellow-700",
    },
    {
      icon: <FaUserSlash size={24} className="text-red-600" />,
      title: "Avg per Buyer",
      value: grandTotalSum,
      change: "",
      color: "bg-red-100",
      textColor: "text-red-600",
      graphColor: "border-red-600",
    },
  ];
  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "#",
        size: 60,
        enableResizing: false,
        enableEditing: false,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "businessName",
        header: "Buyer/Bussiness Name",
        size: 150,
        grow: true, // fixed width
      },
      {
        accessorKey: "registrationNo",
        header: "NTN / CNIC / Resigtration No.",
        grow: true, // takes remaining space
      },
      {
        accessorKey: "totalInvoices",
        header: "Total Invoices",
        grow: true, // takes remaining space
      },
      {
        accessorKey: "subTotalSum",
        header: "Total Amount",
        Cell: ({ cell }) => formatTwoDecimals(cell.getValue()),
        grow: true, // takes remaining space
      },
      {
        accessorKey: "taxTotalSum",
        header: "Total Tax",
        Cell: ({ cell }) => formatTwoDecimals(cell.getValue()),
        grow: true, // takes remaining space
      },
      {
        accessorKey: "grandTotalSum",
        header: "Grand Total",
        Cell: ({ cell }) => formatTwoDecimals(cell.getValue()),
        grow: true, // takes remaining space
      },
    ],
    [],
  );
  const renderMobileBuyerSummaryCards = () => {
    // ✅ Empty check
    if (!buyerSummary || buyerSummary.length === 0) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "#999",
            fontSize: 14,
          }}
        >
          No Buyer Summary
        </Box>
      );
    }

    return buyerSummary.map((item, index) => {
      return (
        <Box
          key={index}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}
        >
          {/* Top Row: Serial */}
          <Box sx={{ fontSize: 12, color: "#999", mb: 1 }}>#{index + 1}</Box>

          {/* Business Name (make it title) */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Buyer Name:</b>
            <Box sx={{ fontSize: 14 }}>{item.businessName || "-"}</Box>
          </Box>

          {/* Registration */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>NTN / CNIC:</b>
            <Box sx={{ fontSize: 14 }}>{item.registrationNo || "-"}</Box>
          </Box>

          {/* Total Invoices */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>
              Total Invoices:
            </b>
            <Box sx={{ fontSize: 14 }}>{item.totalInvoices || 0}</Box>
          </Box>

          {/* Total Amount */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Total Amount:</b>
            <Box sx={{ fontSize: 14 }}>{formatTwoDecimals(item.subTotalSum)}</Box>
          </Box>

          {/* Total Tax */}
          <Box sx={{ mb: 1 }}>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Total Tax:</b>
            <Box sx={{ fontSize: 14 }}>{formatTwoDecimals(item.taxTotalSum)}</Box>
          </Box>

          {/* Grand Total (highlighted) */}
          <Box>
            <b className={`text-[${globalStyle.bgColorBlue}]`}>Grand Total:</b>
            <Box sx={{ fontSize: 15, fontWeight: 600 }}>
              {formatTwoDecimals(item.grandTotalSum)}
            </Box>
          </Box>
        </Box>
      );
    });
  };
  return (
    <div className="w-full min-h-fit bg-gray-50 flex flex-col items-center py-4">
      <div className="w-full">
        <ReportBackButton />
      </div>
      {/* uppar Portion No need to chnage */}
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="w-full space-y-6 mb-8"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Invoice Summary
        </h2>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Company Name */}
          <div>
            <label className="block mb-1 font-medium">
              Company Name<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              disabled={!companiesList || companiesList.length === 0}
              className={`w-full px-4 py-2 border rounded-md ${
                errors.companyId ? "border-red-500" : "border-gray-300"
              }`}
            >
              {!companiesList || companiesList.length === 0 ? (
                <option value="">No Companies Available</option>
              ) : (
                <>
                  <option value="">Select</option>
                  {companiesList.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.companyName}
                    </option>
                  ))}
                </>
              )}
            </SearchableSelect>
            {errors.companyId && (
              <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>
            )}
          </div>

          {/* Start Date */}
          <InputField
            label="Start Date"
            name="startDate"
            max={maxDate}
            type="date"
            value={formData.startDate}
            error={errors.startDate}
            onChange={handleChange}
            required
          />
          {/* End Date */}
          <InputField
            label="End Date"
            name="endDate"
            max={maxDate}
            type="date"
            value={formData.endDate}
            error={errors.endDate}
            onChange={handleChange}
            required
          />
        </div>

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={isGettingInvoiceSummary}
          className="w-full md:w-1/3 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
          style={{ background: globalStyle.buttonColor }}
        >
          {isGettingInvoiceSummary ? "Please Wait..." : "Search"}
        </button>
      </form>
      {/* lower portion need to be below uppar protion */}
      {hasSearched && (
        <>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`rounded-xl p-5 ${stat.color} shadow-sm hover:shadow-md transition`}
              >
                {/* Icon */}
                <div className="flex justify-between items-center mb-3">
                  <div className="p-2 rounded-full bg-white/70">
                    {stat.icon}
                  </div>
                </div>

                {/* Title */}
                <p className="text-sm font-medium text-gray-600">
                  {stat.title}
                </p>

                {/* Value */}
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  {isGettingInvoiceSummary ? (
                    <span className="inline-block h-5 w-5 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></span>
                  ) : (
                    (stat.value ?? 0)
                  )}
                </p>

                {/* Divider */}
                <div
                  className={`mt-4 h-1 w-full rounded-full ${stat.graphColor} opacity-40`}
                />
              </div>
            ))}
          </div>
          <Box
            className={`${isMobile ? "mt-4 mb-8" : "p-4"}`}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <Box
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap", // important for mobile
                gap: 1,
              }}
            >
              <Box>
                <Box
                  component="h1"
                  sx={{
                    fontFamily: globalStyle.fontFamilyHeading,
                    fontWeight: 700,
                    color: globalStyle.bgColorBlue,
                    fontSize: {
                      xs: globalStyle.headingFontSizeSmall, // mobile
                      sm: globalStyle.headingFontSize, // tablet+
                    },
                    lineHeight: 1.2,
                  }}
                >
                  Buyer Summary
                </Box>
              </Box>
            </Box>
            {isMobile ? (
              <MobileViewTabelList
                noPagination={true}
                globalFilter={""}
                setGlobalFilter={() => {}}
                pagination={""}
                setPagination={() => {}}
                totalResults={""}
                loading={isGettingInvoiceSummary}
                searchLabel="Buyer Summary"
              >
                {renderMobileBuyerSummaryCards()}
              </MobileViewTabelList>
            ) : (
              <MaterialReactTable
                columns={columns}
                data={buyerSummary || []}
                state={{
                  isLoading: isGettingInvoiceSummary,
                }}
                muiTablePaperProps={{
                  sx: {
                    backgroundColor: globalStyle.bgColorWhite,
                    borderRadius: `${globalStyle.borderRadius}px`,
                    fontFamily: globalStyle.fontFamily,
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    backgroundColor: globalStyle.blueColor,
                    color: "#fff",
                    fontSize: globalStyle.fontSize,
                    fontFamily: globalStyle.fontFamilyHeading,
                    fontWeight: 600,
                    borderBottom: "none",
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    fontSize: globalStyle.fontSize,
                    fontFamily: globalStyle.fontFamilyParagraph,
                    color: "#333",
                    borderBottom: `1px solid ${globalStyle.curveLightColor}`,
                  },
                }}
                muiTableBodyRowProps={{
                  sx: {
                    backgroundColor: globalStyle.bgColorWhite,
                    "&:hover": {
                      backgroundColor: globalStyle.bgColor,
                    },
                  },
                }}
                muiTableContainerProps={{
                  sx: {
                    maxHeight: "none", // ✅ remove height restriction
                    overflow: "visible", // ✅ allow content to expand
                    borderRadius: `${globalStyle.borderRadius}px`,
                  },
                }}
                muiPaginationProps={{
                  sx: {
                    "& .MuiTablePagination-toolbar": {
                      fontFamily: globalStyle.fontFamily,
                    },
                    "& .Mui-selected": {
                      backgroundColor: globalStyle.blueColor,
                      color: "#fff",
                    },
                  },
                }}
                muiTableProps={{
                  sx: {
                    tableLayout: "fixed",
                  },
                }}
              />
            )}
          </Box>
        </>
      )}
    </div>
  );
};

export default InvoiceSummary;
